import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import { useCreateProductMutation, useGetCategoriesQuery } from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { PRODUCT_UNITS } from '../constants/units';

function QuickProductDialog({ open, onClose, onCreated }) {
  const [createProduct, { isLoading }] = useCreateProductMutation();
  const { data: company } = useGetCurrentCompanyQuery();
  const { data: categoriesData } = useGetCategoriesQuery({ search: '' });

  const currency = company?.currency || 'USD';

  const [formData, setFormData] = useState({
    name: '',
    barcode: '',
    categoryId: null,
    baseUnit: 'pcs',
    sellingPrice: '',
    costPrice: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Product name is required');
      return;
    }

    try {
      const submitData = {
        name: formData.name.trim(),
        barcode: formData.barcode || '',
        categoryId: formData.categoryId || null,
        baseUnit: formData.baseUnit || 'pcs',
        costPrice: parseFloat(formData.costPrice) || 0,
        wholesalePrice: parseFloat(formData.sellingPrice) || 0,
        sellingPrice: parseFloat(formData.sellingPrice) || 0,
        isActive: true,
        type: 'product',
      };

      const created = await createProduct(submitData).unwrap();
      if (onCreated) {
        onCreated(created);
      }
      onClose();
      setFormData({
        name: '',
        barcode: '',
        categoryId: null,
        baseUnit: 'pcs',
        sellingPrice: '',
        costPrice: '',
      });
    } catch (err) {
      console.error('Error creating product:', err);
      alert(err?.data?.message || 'Error creating product');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Quick Create Product</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid  size={{xs:12}}>
              <TextField
                fullWidth
                label="Product Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                autoFocus
              />
            </Grid>
            <Grid size={{xs:12,sm:6}}>
              <TextField
                fullWidth
                label="Barcode (Optional)"
                name="barcode"
                value={formData.barcode}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{xs:12,sm:6}}>
              <Autocomplete
                options={
                  categoriesData
                    ? [
                        ...categoriesData.filter((c) => c.parentId !== null),
                        ...categoriesData.filter((c) => c.parentId === null),
                      ]
                    : []
                }
                getOptionLabel={(option) =>
                  option.parent?.name
                    ? `${option.parent.name} > ${option.name}`
                    : `${option.name} (Main Category)`
                }
                value={categoriesData?.find((c) => c.id === formData.categoryId) || null}
                onChange={(event, newValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    categoryId: newValue ? newValue.id : null,
                  }));
                }}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={(params) => (
                  <TextField {...params} label="Category (Optional)" />
                )}
              />
            </Grid>
            <Grid size={{xs:12,sm:4}}>
              <TextField
                fullWidth
                select
                label="Base Unit"
                name="baseUnit"
                value={formData.baseUnit}
                onChange={handleChange}
              >
                {PRODUCT_UNITS.map((unit) => (
                  <MenuItem key={unit.value} value={unit.value}>
                    {unit.label}
                  </MenuItem>
                ))}
                {formData.baseUnit && !PRODUCT_UNITS.some((u) => u.value === formData.baseUnit) && (
                  <MenuItem key={formData.baseUnit} value={formData.baseUnit}>
                    {formData.baseUnit}
                  </MenuItem>
                )}
              </TextField>
            </Grid>
            <Grid size={{xs:12,sm:4}}>
              <TextField
                fullWidth
                label={`Cost Price (${currency})`}
                name="costPrice"
                type="number"
                inputProps={{ step: 0.01, min: 0 }}
                value={formData.costPrice}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{xs:12,sm:4}}>
              <TextField
                fullWidth
                label={`Selling Price (${currency})`}
                name="sellingPrice"
                type="number"
                inputProps={{ step: 0.01, min: 0 }}
                value={formData.sellingPrice}
                onChange={handleChange}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default QuickProductDialog;
